import { Link, useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FEEDBACK_ENTRY_TYPES,
  evaluateRequestMoreInfoEligibility,
  feedbackEntryTypeLabel,
  feedbackEntryTypePluralLabel,
  rbac,
} from '@dha-nexus/shared';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { usePageMetadata } from '../../app/PageMetaContext.jsx';
import { api } from '../../lib/apiClient.js';
import { useApi, useMutation } from '../../lib/useApi.js';
import {
  DateInput,
  EmptyState,
  ErrorState,
  FormField,
  KpiHeroBand,
  LoadingSkeleton,
  PageHeader,
  Select,
  StatusChip,
  StatusDonutMini,
  StaggerReveal,
  TextInput,
  Textarea,
  WorkflowPipeline,
  buildPipelineSteps,
} from '../../components/index.js';
import { PersonLabel } from '../../components/primitives/PersonLabel.jsx';
import { entityTypeLabel, humanizeEnum, roleLabel } from '../../lib/fieldLabels.js';
import { CATEGORY_OPTIONS as CANONICAL_CATEGORY_OPTIONS } from '../feedbackMarkup/AnnotationDetailsPanel.jsx';
import { GovernedAnnotationPreview } from './GovernedAnnotationPreview.jsx';
import { TypedIntakeDetailSections } from './TypedIntakeDetailSections.jsx';
import {
  convertGovernedFeedback,
  FEEDBACK_COMMENT_MAX_LENGTH,
  listGovernedFeedback,
  requestGovernedFeedbackInfo,
} from '../feedbackMarkup/feedbackApi.js';
import {
  clearPendingFeedbackMutation,
  feedbackMutationSignature,
  pendingFeedbackMutation,
  shouldRetainFeedbackMutation,
} from '../feedbackMarkup/feedbackIdempotency.js';
import './feedbackOps.css';
import './feedbackOps.redesign.css';

// A small monospace technical reference for the raw element key. The human
// elementLabel is the primary label; the componentKey stays visible for
// engineers as a machine reference (H2).
const COMPONENT_KEY_STYLE = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
  fontSize: '0.72rem',
  opacity: 0.72,
};

// Legacy category values that predate the canonical vocabulary but still appear
// on historical/seeded feedback records — retained so those records stay
// selectable during triage even though new captures use the canonical set.
const LEGACY_CATEGORY_VALUES = ['ux', 'workflow', 'training'];

const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'triaged', label: 'Triaged' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'needs_more_info', label: 'Needs more info' },
  { value: 'converted_to_action', label: 'Converted to action' },
  { value: 'converted_to_issue', label: 'Converted to issue' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'deferred', label: 'Deferred' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'duplicate', label: 'Duplicate' },
];

const SEVERITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const CONVERSION_PRIORITY_OPTIONS = SEVERITY_OPTIONS;

const RELATED_RECORD_TYPE_OPTIONS = [
  { value: '', label: 'None linked' },
  { value: 'action', label: 'Action' },
  { value: 'work_item', label: 'Work item' },
  { value: 'issue', label: 'Issue' },
  { value: 'risk', label: 'Risk' },
  { value: 'project', label: 'Project' },
];

const QUEUE_VIEW_OPTIONS = [
  { value: 'all_feedback', label: 'All feedback' },
  { value: 'new_feedback', label: 'New feedback' },
  { value: 'my_assigned_feedback', label: 'My assigned feedback' },
  { value: 'high_severity_feedback', label: 'Critical / high severity' },
  { value: 'by_route_page', label: 'By route / page' },
  { value: 'by_component', label: 'By component' },
  { value: 'by_feature_area', label: 'By feature area' },
  { value: 'by_uat_cohort', label: 'By UAT cohort' },
  { value: 'converted_feedback', label: 'Converted feedback' },
  { value: 'resolved_feedback', label: 'Resolved feedback' },
  { value: 'deferred_feedback', label: 'Deferred feedback' },
];

const ENVIRONMENT_OPTIONS = [
  { value: '', label: 'All environments' },
  { value: 'pmhub', label: 'PMHub' },
  { value: 'uat-01', label: 'UAT-01' },
  { value: 'uat-02', label: 'UAT-02' },
  { value: 'uat-03', label: 'UAT-03' },
  { value: 'uat-04', label: 'UAT-04' },
  { value: 'uat-05', label: 'UAT-05' },
];

// Typed-intake queue facets (commit 6). Filtering happens SERVER SIDE against the
// migration-025 projection columns, so the list, `page.total`, and the KPI band
// all describe the same slice.
const ENTRY_TYPE_OPTIONS = [
  { value: '', label: 'All entry types' },
  ...FEEDBACK_ENTRY_TYPES.map((entryType) => ({
    value: entryType,
    label: feedbackEntryTypePluralLabel(entryType),
  })),
];

// Tri-state, matching the server's `hasIntake`: absent means "do not filter", so
// the default cannot be mistaken for an explicit "no intake".
const INTAKE_LINK_OPTIONS = [
  { value: '', label: 'Any origin' },
  { value: 'true', label: 'From a typed request' },
  { value: 'false', label: 'From screen capture only' },
];

const DEFAULT_FILTERS = Object.freeze({
  queueView: 'all_feedback',
  environmentId: '',
  status: '',
  severity: '',
  routeQuery: '',
  componentQuery: '',
  featureArea: '',
  uatCohort: '',
  entryType: '',
  hasIntake: '',
  sourceIntakeId: '',
});

function humanizeToken(value, fallback = 'Unspecified') {
  if (!value) return fallback;
  return String(value)
    .replace(/[._-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function entityLabel(item) {
  if (!item.entityType && !item.entityId) return null;
  // H1 — humanize the raw entity type through the shared label helper so the
  // card and detail read "Intake request · REQ-…" instead of "intake_request · …".
  return [entityTypeLabel(item.entityType) || 'Entity', item.entityId || 'Pending'].join(' · ');
}

function reporterLabel(item) {
  // Humanize the role id so the reporter reads in plain language (e.g.
  // "ROLE-PORTFOLIO-MANAGER" -> "Portfolio Manager"); already-humanized personas
  // pass through unchanged.
  const parts = [item.submittedByDisplayName, roleLabel(item.submittedByRole)].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

function relatedRecordDescriptor(item) {
  const type = item?.linkedRecordType || item?.convertedEntityType || null;
  const id = item?.linkedRecordId || item?.convertedEntityId || null;
  return { type, id };
}

function relatedRecordHref(item) {
  const related = relatedRecordDescriptor(item);
  if (!related.type || !related.id) return null;
  if (related.type === 'action') return `/actions/${related.id}`;
  if (related.type === 'work_item') return `/work-items/${related.id}`;
  if (related.type === 'issue') return '/governance/issues';
  if (related.type === 'risk') return '/governance/risks';
  if (related.type === 'project') return `/projects/${related.id}`;
  return null;
}

function summarizeAuditEvent(event) {
  return (
    event?.payload?.summary ||
    event?.summary ||
    (event?.action ? humanizeToken(event.action, 'Audit event recorded') : 'Audit event recorded')
  );
}

const AUDIT_CHANGE_FIELDS = [
  ['status', 'Status'],
  ['severity', 'Severity'],
  ['category', 'Category'],
  ['ownerUserId', 'Owner'],
  ['uatCohort', 'UAT cohort'],
  ['linkedRecordType', 'Related record type'],
  ['linkedRecordId', 'Related record ID'],
  ['resolutionNote', 'Resolution note'],
];

const HUMANIZED_AUDIT_FIELDS = new Set([
  'status',
  'severity',
  'category',
  'uatCohort',
  'linkedRecordType',
]);

function auditFieldValue(field, value) {
  if (value === null || value === undefined || value === '') return 'Not set';
  if (typeof value === 'string' && HUMANIZED_AUDIT_FIELDS.has(field)) {
    return humanizeToken(value, value);
  }
  return String(value);
}

function summarizeAuditChanges(event) {
  const before = event?.payload?.before;
  const after = event?.payload?.after;
  if (!before || !after) return null;

  const changes = AUDIT_CHANGE_FIELDS.flatMap(([field, label]) => {
    if (
      !Object.prototype.hasOwnProperty.call(before, field) ||
      !Object.prototype.hasOwnProperty.call(after, field)
    ) {
      return [];
    }
    if (before[field] === after[field]) return [];
    return [
      `${label}: ${auditFieldValue(field, before[field])} → ${auditFieldValue(field, after[field])}`,
    ];
  });

  return changes.length ? `Changed fields: ${changes.join('; ')}` : null;
}

function formatTimestamp(value) {
  if (!value) return 'Timestamp unavailable';
  return new Date(value).toLocaleString();
}

function isConvertedStatus(status) {
  return status === 'converted_to_action' || status === 'converted_to_issue';
}

function canConvertFeedback(status) {
  return ['new', 'triaged', 'accepted'].includes(status);
}

// Map a severity level onto an executive-traffic-light tone. Critical/high read
// as brick, medium as amber, low as blue, unknown as slate.
function severityTone(severity) {
  switch (severity) {
    case 'critical':
    case 'high':
      return 'brick';
    case 'medium':
      return 'amber';
    case 'low':
      return 'blue';
    default:
      return 'slate';
  }
}

// A new or critical record gets the attention-pulsing dot.
function isUrgentRecord(item) {
  return item?.status === 'new' || item?.severity === 'critical';
}

// Severity distribution segments for the StatusDonutMini (data-driven; omits
// any zero-count band). Ordered most-to-least urgent for legend legibility.
function severitySegments(rows) {
  const counts = { critical: 0, high: 0, medium: 0, low: 0, unspecified: 0 };
  rows.forEach((row) => {
    const key = Object.prototype.hasOwnProperty.call(counts, row.severity)
      ? row.severity
      : 'unspecified';
    counts[key] += 1;
  });
  return [
    { label: 'Critical', count: counts.critical, tone: 'brick' },
    { label: 'High', count: counts.high, tone: 'brick' },
    { label: 'Medium', count: counts.medium, tone: 'amber' },
    { label: 'Low', count: counts.low, tone: 'blue' },
    { label: 'Unspecified', count: counts.unspecified, tone: 'slate' },
  ].filter((segment) => segment.count > 0);
}

// Collapse the rich governed status set onto the four-stage triage lifecycle
// the boardroom pipeline visualizes: new -> triaged -> converted -> resolved.
// Rejected / duplicate / deferred terminal states surface as a blocked stage.
const TRIAGE_PIPELINE_ORDER = ['new', 'triaged', 'converted', 'resolved'];
const TRIAGE_PIPELINE_LABELS = {
  new: 'New',
  triaged: 'Triaged',
  converted: 'Converted',
  resolved: 'Resolved',
};

function pipelineStageForStatus(status) {
  if (!status) return 'new';
  if (status === 'new') return 'new';
  if (status === 'resolved') return 'resolved';
  if (isConvertedStatus(status) || status === 'in_progress') return 'converted';
  return 'triaged';
}

function isBlockedStatus(status) {
  return status === 'rejected' || status === 'duplicate' || status === 'deferred';
}

function buildTriagePipeline(item) {
  const status = item?.status || 'new';
  const current = pipelineStageForStatus(status);
  const statusByKey = {};
  if (isBlockedStatus(status)) {
    statusByKey[current] = 'blocked';
  }
  return buildPipelineSteps({
    order: TRIAGE_PIPELINE_ORDER,
    current,
    statusByKey,
    labels: TRIAGE_PIPELINE_LABELS,
  });
}

function buildTriageDraft(item) {
  return {
    ownerUserId: item?.ownerUserId || '',
    status: item?.status || 'new',
    severity: item?.severity || 'medium',
    category: item?.category || 'ux',
    uatCohort: item?.uatCohort || '',
    linkedRecordType: item?.linkedRecordType || item?.convertedEntityType || '',
    linkedRecordId: item?.linkedRecordId || item?.convertedEntityId || '',
    resolutionNote: item?.resolutionNote || '',
  };
}

function buildConversionDraft(item) {
  return {
    ownerUserId: item?.ownerUserId || item?.reporterUserId || '',
    priority: ['low', 'medium', 'high', 'critical'].includes(item?.severity)
      ? item.severity
      : 'medium',
    dueDate: item?.dueDate || '',
  };
}

export function mergeUntouchedDraft(current, dirtyFields, nextBaseline) {
  const next = { ...nextBaseline };
  for (const field of dirtyFields || []) {
    if (Object.prototype.hasOwnProperty.call(current || {}, field)) next[field] = current[field];
  }
  return next;
}

function draftsMatch(left, right) {
  const keys = new Set([...Object.keys(left || {}), ...Object.keys(right || {})]);
  return [...keys].every((key) => left?.[key] === right?.[key]);
}

function updateListEnvelope(current, updatedRecord) {
  if (!current || !Array.isArray(current.data)) return current;
  return {
    ...current,
    data: current.data.map((row) =>
      row.id === updatedRecord.id ? { ...row, ...updatedRecord } : row,
    ),
  };
}

function categoryOptions(rows, detailItem) {
  // H3 — base the vocabulary on the canonical element-feedback category list
  // shared with the capture drawer, plus any legacy or data-observed values so
  // historical records stay selectable. Labels come from the canonical list
  // first, then the shared humanizeEnum ("ux" -> "UX", "ux_confusion" -> "UX
  // confusion") — never the wrong-cased local humanizer.
  const values = new Set([
    ...CANONICAL_CATEGORY_OPTIONS.map((option) => option.value),
    ...LEGACY_CATEGORY_VALUES,
  ]);
  [...rows, detailItem].forEach((item) => {
    if (item?.category) values.add(item.category);
  });
  return Array.from(values).map((value) => {
    const canonical = CANONICAL_CATEGORY_OPTIONS.find((option) => option.value === value);
    return { value, label: canonical?.label || humanizeEnum(value, value) };
  });
}

function userOptions(users) {
  return users.map((user) => ({
    value: user.id,
    label: user.displayName || user.id,
  }));
}

function FeedbackCard({ item, selected, onSelect }) {
  const entity = entityLabel(item);
  const reporter = reporterLabel(item);
  const tone = severityTone(item.severity);
  const urgent = isUrgentRecord(item);
  const severityLabel = humanizeToken(item.severity, 'Unspecified');

  return (
    <article
      className={`feedback-ops-card fbk-rdx-card tone-${tone}${selected ? ' is-selected' : ''}`}
    >
      <button
        type="button"
        className="feedback-ops-card-button"
        onClick={() => onSelect(item.id)}
        aria-pressed={selected}
      >
        <div className="row spread" style={{ gap: 12 }}>
          <div>
            <div className="fbk-source-line">
              <span className="eyebrow">{item.originId || item.id}</span>
              <span className="fbk-source-badge">
                {String(item.environmentId || 'pmhub').toUpperCase()}
              </span>
            </div>
            <h3 style={{ margin: '4px 0 6px' }}>{item.title}</h3>
          </div>
          <div className="fbk-rdx-card-chips">
            <span className={`chip ${tone} sm`}>
              <span className={`chip-dot${urgent ? ' pulse' : ''}`} />
              {severityLabel}
            </span>
            <StatusChip status={item.status || item.severity || 'new'} />
          </div>
        </div>
        <p className="muted">{item.description}</p>
        <div className="feedback-ops-meta">
          <span>{item.pageTitle || item.screenContext?.pageTitle || 'Page unavailable'}</span>
          <span>{item.routePath || 'Route unavailable'}</span>
          {/* H2 — human element label first; keep the raw componentKey as a small
              monospace machine reference beside it (never as the only label). */}
          <span>{item.elementLabel || item.componentKey || 'Component unavailable'}</span>
          {item.elementLabel && item.componentKey ? (
            <span style={COMPONENT_KEY_STYLE}>{item.componentKey}</span>
          ) : null}
          {entity ? <span>{entity}</span> : null}
          {reporter ? <span>{reporter}</span> : null}
          {item.uatCohort ? <span>{item.uatCohort}</span> : null}
          {/* Which authoring surface produced this record (commit 6), from the
              shared label chokepoint. Shown only for typed intakes: labelling
              every screenshot card "Screen feedback" would add noise to the
              queue's overwhelmingly common case without telling a reviewer
              anything they did not already know from the capture preview. */}
          {item.entryType && item.entryType !== 'screen_feedback' ? (
            <span data-testid={`feedback-card-entry-type-${item.id}`}>
              {feedbackEntryTypeLabel(item.entryType)}
            </span>
          ) : null}
        </div>
        <div className="feedback-ops-card-footer">
          <span className="feedback-ops-card-footnote">
            {item.ownerUserId ? (
              <>
                Owner <PersonLabel userId={item.ownerUserId} />
              </>
            ) : (
              'Owner pending'
            )}
          </span>
          <span className="feedback-ops-card-cta">
            {selected ? 'Detail selected' : 'Open detail'}
          </span>
        </div>
      </button>
    </article>
  );
}

export function FeedbackQueuePage() {
  usePageMetadata({
    title: 'Feedback Queue',
    subtitle: 'Governed UI feedback triage, conversion, and follow-through for bake-off readiness.',
  });

  const { user, roles } = useCurrentUser();
  // W3B: triage power (status/comment/convert) is System-Admin-only on the
  // server (isFeedbackReviewer); mirror that here so the six queue roles see
  // a read-only queue instead of controls that silently 403.
  const isReviewer = Array.isArray(roles) && roles.includes(rbac.ROLE_IDS.SYSTEM_ADMIN);
  const usersRes = useApi('/users', { query: { limit: 200 } }) || {};
  const aggregateStatus =
    useApi(isReviewer ? '/feedback/aggregate-status' : null, { skip: !isReviewer }) || {};
  const aggregateContext =
    useApi(isReviewer ? '/feedback/aggregate-context' : null, { skip: !isReviewer }) || {};
  const mutation = useMutation();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [queue, setQueue] = useState({
    rows: [],
    page: { total: 0, nextCursor: null, hasMore: false, limit: 50 },
    meta: { kpis: { total: 0, new: 0, highSeverity: 0, converted: 0, resolved: 0 } },
    loading: true,
    loadingMore: false,
    error: null,
    appendError: null,
    loadedAt: null,
  });
  const requestSequence = useRef(0);
  // Deep link seam (PM guide results workspace and other evidence links):
  // ?record=<feedback-id> pins the initial selection to that record even when
  // it is not on the first queue page — the detail fetch goes straight to
  // /feedback/<id>, so the reading pane still resolves.
  const [searchParams] = useSearchParams();
  const deepLinkRecordId = searchParams.get('record') || null;
  const pinnedRecordRef = useRef(deepLinkRecordId);
  const [selectedId, setSelectedId] = useState(deepLinkRecordId);
  const selectRecord = useCallback((recordId) => {
    pinnedRecordRef.current = null;
    setSelectedId(recordId);
    // A success/error banner about the previous record must not sit above an
    // unrelated one the reviewer explicitly navigated to. (Programmatic
    // selection restores — e.g. after a refresh — keep fresh notices.)
    setActionNotice(null);
  }, []);
  // In-page deep-link changes (?record=...) must move the selection too — the
  // component does not remount when only the query string changes.
  const lastDeepLinkRef = useRef(deepLinkRecordId);
  useEffect(() => {
    if (deepLinkRecordId && deepLinkRecordId !== lastDeepLinkRef.current) {
      pinnedRecordRef.current = deepLinkRecordId;
      setSelectedId(deepLinkRecordId);
    }
    lastDeepLinkRef.current = deepLinkRecordId;
  }, [deepLinkRecordId]);
  const [triageDraft, setTriageDraft] = useState(buildTriageDraft(null));
  const [conversionDraft, setConversionDraft] = useState(buildConversionDraft(null));
  const dirtyDraftFieldsRef = useRef({ triage: new Set(), conversion: new Set() });
  const updateTriageDraft = useCallback((field, value) => {
    dirtyDraftFieldsRef.current.triage.add(field);
    setTriageDraft((current) => ({ ...current, [field]: value }));
  }, []);
  const updateConversionDraft = useCallback((field, value) => {
    dirtyDraftFieldsRef.current.conversion.add(field);
    setConversionDraft((current) => ({ ...current, [field]: value }));
  }, []);
  const [triageNote, setTriageNote] = useState('');
  const [actionNotice, setActionNotice] = useState(null);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [cleanupConfirmation, setCleanupConfirmation] = useState('');
  const [cleanupReason, setCleanupReason] = useState('');
  // Focus target after destructive-action success: the cleanup button
  // unmounts with the detail pane, so keyboard focus needs a landing spot.
  const noticeRef = useRef(null);

  const queueQuery = useMemo(
    () => ({
      view: filters.queueView,
      environmentId: filters.environmentId || undefined,
      status: filters.status || undefined,
      severity: filters.severity || undefined,
      routeQuery: filters.routeQuery || undefined,
      componentQuery: filters.componentQuery || undefined,
      featureQuery: filters.featureArea || undefined,
      cohortQuery: filters.uatCohort || undefined,
      entryType: filters.entryType || undefined,
      hasIntake: filters.hasIntake || undefined,
      sourceIntakeId: filters.sourceIntakeId || undefined,
      limit: 50,
    }),
    [filters],
  );
  const queueQueryKey = JSON.stringify(queueQuery);

  const loadQueuePage = useCallback(
    async ({ cursor = null, append = false, clearExisting = false } = {}) => {
      const sequence = ++requestSequence.current;
      setQueue((current) => ({
        ...current,
        loading: !append,
        loadingMore: append,
        error: append ? current.error : null,
        appendError: null,
        ...(append || !clearExisting
          ? {}
          : { rows: [], page: { total: 0, nextCursor: null, hasMore: false, limit: 50 } }),
      }));
      try {
        const response = await listGovernedFeedback({
          ...queueQuery,
          ...(cursor ? { cursor } : {}),
        });
        if (sequence !== requestSequence.current) return;
        // apiClient returns the API envelope directly. Do not unwrap `data` a
        // second time: doing so turns a valid queue response into an empty list.
        const incomingRows = response?.data || [];
        const page = response?.page || {};
        const meta = response?.meta || {};
        setQueue((current) => {
          const rows = append
            ? [
                ...current.rows,
                ...incomingRows.filter((row) => !current.rows.some((item) => item.id === row.id)),
              ]
            : incomingRows;
          return {
            ...current,
            rows,
            page: {
              total: Number(page.total || 0),
              nextCursor: page.nextCursor || null,
              hasMore: Boolean(page.hasMore),
              limit: Number(page.limit || 50),
            },
            meta,
            loading: false,
            loadingMore: false,
            error: null,
            appendError: null,
            loadedAt: new Date(),
          };
        });
      } catch (error) {
        if (sequence !== requestSequence.current) return;
        setQueue((current) => ({
          ...current,
          loading: false,
          loadingMore: false,
          error: append ? current.error : error,
          appendError: append ? error : null,
        }));
      }
    },
    [queueQuery],
  );

  useEffect(() => {
    // A filter change invalidates the old page and selection, except for a
    // still-pinned deep-linked record (which resolves via /feedback/<id>).
    setSelectedId((current) =>
      pinnedRecordRef.current && current === pinnedRecordRef.current ? current : null,
    );
    loadQueuePage({ clearExisting: true });
    return () => {
      requestSequence.current += 1;
    };
  }, [loadQueuePage, queueQueryKey]);

  // Refresh must always run the LATEST query — a refresh captured before a
  // filter change would otherwise repopulate the list with old-filter rows.
  const loadQueuePageRef = useRef(loadQueuePage);
  useEffect(() => {
    loadQueuePageRef.current = loadQueuePage;
  }, [loadQueuePage]);

  const feedback = useMemo(
    () => ({
      data: { data: queue.rows, page: queue.page, meta: queue.meta },
      loading: queue.loading,
      error: queue.error,
      loadedAt: queue.loadedAt,
      refresh: () => loadQueuePageRef.current(),
      setData: (updater) => {
        setQueue((current) => {
          const currentData = { data: current.rows, page: current.page, meta: current.meta };
          const nextData = typeof updater === 'function' ? updater(currentData) : updater;
          return {
            ...current,
            rows: nextData?.data || [],
            page: nextData?.page || current.page,
            meta: nextData?.meta || current.meta,
          };
        });
      },
    }),
    [queue],
  );

  const rows = queue.rows;
  const filteredRows = rows;

  const detail =
    useApi(selectedId ? `/feedback/${selectedId}` : null, {
      skip: !selectedId,
    }) || {};
  const detailRecord =
    detail.data?.data?.id && detail.data.data.id === selectedId ? detail.data.data : null;

  useEffect(() => {
    // A pinned deep-linked record keeps its selection even when it is not in
    // the loaded page; any manual selection change releases the pin. A
    // selection whose detail is already loaded is also kept — a post-mutation
    // refresh that collapses back to page one must not silently switch the
    // reading pane to an unrelated record.
    if (pinnedRecordRef.current && selectedId === pinnedRecordRef.current) return;
    if (selectedId && detailRecord) return;
    if (!filteredRows.length) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !filteredRows.some((row) => row.id === selectedId)) {
      setSelectedId(filteredRows[0].id);
    }
  }, [detailRecord, filteredRows, selectedId]);

  const detailItem = useMemo(
    () => detailRecord || filteredRows.find((row) => row.id === selectedId) || null,
    [detailRecord, filteredRows, selectedId],
  );
  // The skeleton must yield to the error state: gating on !detailRecord alone
  // kept a failed detail fetch (purged mirror deep link, forbidden record)
  // spinning forever with the error branch unreachable.
  const detailLoadingForSelection =
    Boolean(selectedId) && (detail.loading || (!detailRecord && !detail.error));
  const draftSourceRef = useRef({
    selectedId: null,
    hasDetailRecord: false,
    triageBaseline: null,
    conversionBaseline: null,
  });

  useEffect(() => {
    const hasDetailRecord = Boolean(detailRecord);
    const source = draftSourceRef.current;
    const selectedRecordChanged = source.selectedId !== selectedId;
    const nextTriageBaseline = buildTriageDraft(detailItem);
    const nextConversionBaseline = buildConversionDraft(detailItem);
    const detailBaselineChanged =
      hasDetailRecord &&
      (!source.hasDetailRecord ||
        !draftsMatch(source.triageBaseline, nextTriageBaseline) ||
        !draftsMatch(source.conversionBaseline, nextConversionBaseline));
    if (!selectedRecordChanged && !detailBaselineChanged) return;

    if (selectedRecordChanged) {
      dirtyDraftFieldsRef.current.triage.clear();
      dirtyDraftFieldsRef.current.conversion.clear();
      setTriageDraft(nextTriageBaseline);
      setConversionDraft(nextConversionBaseline);
      setTriageNote('');
      // The destructive cleanup form must never carry a typed confirmation or
      // reason from one record to another — a stale reason would be persisted
      // into the wrong package's audit trail.
      setCleanupOpen(false);
      setCleanupConfirmation('');
      setCleanupReason('');
    } else {
      setTriageDraft((current) =>
        mergeUntouchedDraft(current, dirtyDraftFieldsRef.current.triage, nextTriageBaseline),
      );
      setConversionDraft((current) =>
        mergeUntouchedDraft(
          current,
          dirtyDraftFieldsRef.current.conversion,
          nextConversionBaseline,
        ),
      );
    }

    draftSourceRef.current = {
      selectedId,
      hasDetailRecord,
      triageBaseline: nextTriageBaseline,
      conversionBaseline: nextConversionBaseline,
    };
  }, [detailItem, detailRecord, selectedId]);

  const categories = useMemo(() => categoryOptions(rows, detailItem), [detailItem, rows]);
  const ownerChoices = useMemo(() => userOptions(usersRes.data?.data || []), [usersRes.data]);

  const severityDistribution = useMemo(() => severitySegments(rows), [rows]);
  const kpiItems = useMemo(() => {
    const totals = queue.meta?.kpis || {};
    const total = Number(totals.total || 0);
    const newCount = Number(totals.new || 0);
    const converted = Number(totals.converted || 0);
    const resolved = Number(totals.resolved || 0);
    const highSeverity = Number(totals.highSeverity || 0);
    const triagedAccepted = Math.max(0, total - newCount - converted - resolved);
    // Sparklines visualize each KPI's share of the queue without fabricating a
    // time series: [remaining, this-bucket] reads as a simple proportion bar.
    const share = (n) => [Math.max(0, total - n), n];
    return [
      {
        id: 'open-queue',
        label: 'Open queue',
        value: total,
        footer: `${newCount} new · ${highSeverity} critical/high`,
        sparkline: share(newCount),
        sparklineTone: 'blue',
        status: 'blue',
      },
      {
        id: 'triaged-accepted',
        label: 'Triaged / accepted',
        value: triagedAccepted,
        footer: total ? `${Math.round((triagedAccepted / total) * 100)}% of queue` : 'No records',
        sparkline: share(triagedAccepted),
        sparklineTone: 'amber',
        status: triagedAccepted ? 'amber' : 'slate',
      },
      {
        id: 'converted',
        label: 'Converted',
        value: converted,
        footer: `${resolved} resolved`,
        sparkline: share(converted),
        sparklineTone: 'sage',
        status: converted ? 'sage' : 'slate',
      },
    ];
  }, [queue.meta]);

  const hasActiveFilters = useMemo(
    () =>
      Object.entries(filters).some(([key, value]) =>
        key === 'queueView' ? value !== 'all_feedback' : Boolean(value),
      ),
    [filters],
  );

  function updateFilter(key, value) {
    setFilters((current) => ({ ...current, [key]: value }));
  }

  function clearFilters() {
    setFilters(DEFAULT_FILTERS);
  }

  function syncUpdatedFeedback(updatedRecord) {
    feedback.setData((current) => updateListEnvelope(current, updatedRecord));
    detail.setData((current) => {
      if (!current?.data) return current;
      return {
        ...current,
        data: {
          ...current.data,
          ...updatedRecord,
          comments: current.data.comments || [],
          auditHistory: current.data.auditHistory || [],
        },
      };
    });
  }

  // A committed mutation must never be reported as a failure because its
  // post-commit refresh could not run: the optimistic sync already reflects the
  // change and the server stays authoritative, so a refresh error is swallowed
  // here instead of thrown back into the mutation's catch (where it would flip
  // a real success to a red banner and, for keyed mutations, strand a cleared
  // idempotency key so a retry re-runs the committed write).
  async function refreshQueueAndDetail() {
    try {
      await Promise.all([feedback.refresh(), detail.refresh()]);
    } catch {
      /* best-effort — the mutation committed; the view is optimistically synced */
    }
  }

  async function saveTriageChanges() {
    if (!selectedId) return;
    setActionNotice(null);
    const payload = {
      status: triageDraft.status,
      severity: triageDraft.severity,
      category: triageDraft.category,
      ownerUserId: triageDraft.ownerUserId || null,
      linkedRecordType: triageDraft.linkedRecordType || null,
      linkedRecordId: triageDraft.linkedRecordId || null,
      uatCohort: triageDraft.uatCohort || null,
      resolutionNote: triageDraft.resolutionNote || null,
    };
    let response;
    try {
      response = await mutation.run(() => api.patch(`/feedback/${selectedId}`, payload));
    } catch (error) {
      setActionNotice({
        tone: 'red',
        title: 'Could not update this feedback record.',
        message: error?.message || 'Unknown error',
      });
      return;
    }
    // Committed — the post-commit steps below run outside the mutation guard.
    dirtyDraftFieldsRef.current.triage.clear();
    syncUpdatedFeedback(response.data);
    setActionNotice({
      tone: 'green',
      title: 'Triage saved',
      message: `${response.data.title || response.data.id} was updated in the governed queue.`,
    });
    await refreshQueueAndDetail();
  }

  // W3B: request-more-info in one gesture — the status flips to
  // needs_more_info and the question lands in the thread the reporter sees.
  async function requestMoreInfo() {
    if (!selectedId) return;
    // Round 3 (R3-H02): the handler repeats the eligibility guard so even a
    // programmatic invocation against an ineligible record sends no request.
    // Eligibility reads the PERSISTED record status, never the unsaved
    // workflow-status dropdown value.
    if (!evaluateRequestMoreInfoEligibility(detailItem?.status).eligible) return;
    const body = triageNote.trim();
    // Match the server's shared comment bound: the note becomes a thread
    // comment, so oversize text must be refused inline, not sent to a 400.
    if (!body || body.length > FEEDBACK_COMMENT_MAX_LENGTH) return;
    const mutationIdentity = {
      actorUserId: user?.id,
      operation: 'request-more-info',
      resourceId: selectedId,
    };
    const pending = pendingFeedbackMutation({
      ...mutationIdentity,
      signature: feedbackMutationSignature({ feedbackId: selectedId, body }),
    });
    setActionNotice(null);
    let response;
    try {
      response = await mutation.run(() =>
        requestGovernedFeedbackInfo(selectedId, { body, idempotencyKey: pending.key }),
      );
    } catch (error) {
      if (!shouldRetainFeedbackMutation(error)) clearPendingFeedbackMutation(mutationIdentity);
      setActionNotice({
        tone: 'red',
        title: 'Could not request more info.',
        message: error?.message || 'Unknown error',
      });
      return;
    }
    // Committed — clear the key and record success before the best-effort
    // refresh, so a refresh failure can never be reported as a request failure.
    clearPendingFeedbackMutation(mutationIdentity);
    syncUpdatedFeedback(response.data.feedback);
    setTriageNote('');
    setActionNotice({
      tone: 'green',
      title: 'More info requested',
      message: 'The reporter sees your question under My feedback and can reply there.',
    });
    await refreshQueueAndDetail();
  }

  async function addTriageNote() {
    if (!selectedId) return;
    const body = triageNote.trim();
    if (!body || body.length > FEEDBACK_COMMENT_MAX_LENGTH) return;
    // The comment route now REQUIRES a UUID Idempotency-Key. Retain one key per
    // logical note (session-storage keyed by actor + record + body signature)
    // so a retry after a lost response replays the ORIGINAL comment instead of
    // posting a duplicate — the same pattern the cleanup flow uses.
    const mutationIdentity = {
      actorUserId: user?.id,
      operation: 'feedback-comment',
      resourceId: selectedId,
    };
    const pending = pendingFeedbackMutation({
      ...mutationIdentity,
      signature: feedbackMutationSignature({ feedbackId: selectedId, body }),
    });
    setActionNotice(null);
    let response;
    try {
      response = await mutation.run(() =>
        api.post(
          `/feedback/${selectedId}/comment`,
          { body },
          { headers: { 'Idempotency-Key': pending.key } },
        ),
      );
    } catch (error) {
      if (!shouldRetainFeedbackMutation(error)) clearPendingFeedbackMutation(mutationIdentity);
      setActionNotice({
        tone: 'red',
        title: 'Could not update this feedback record.',
        message: error?.message || 'Unknown error',
      });
      return;
    }
    // Committed — clear the key and record success before the best-effort
    // refresh so a failed reload never flips the added note to a red banner.
    clearPendingFeedbackMutation(mutationIdentity);
    detail.setData((current) => {
      if (!current?.data) return current;
      return {
        ...current,
        data: {
          ...current.data,
          comments: [...(current.data.comments || []), response.data],
        },
      };
    });
    setTriageNote('');
    setActionNotice({
      tone: 'green',
      title: 'Triage note added',
      message: 'The feedback discussion now includes the latest triage note.',
    });
    try {
      await detail.refresh();
    } catch {
      /* best-effort — the comment committed and is already reflected locally */
    }
  }

  async function convertFeedback(targetType) {
    if (!selectedId) return;
    const payload = {
      targetType,
      ownerUserId: conversionDraft.ownerUserId || null,
      priority: conversionDraft.priority || 'medium',
      dueDate: conversionDraft.dueDate || null,
    };
    const mutationIdentity = {
      actorUserId: user?.id,
      operation: 'convert',
      resourceId: selectedId,
    };
    const pending = pendingFeedbackMutation({
      ...mutationIdentity,
      signature: feedbackMutationSignature({ feedbackId: selectedId, ...payload }),
    });
    setActionNotice(null);
    let response;
    try {
      response = await mutation.run(() =>
        convertGovernedFeedback(selectedId, { ...payload, idempotencyKey: pending.key }),
      );
    } catch (error) {
      if (!shouldRetainFeedbackMutation(error)) clearPendingFeedbackMutation(mutationIdentity);
      setActionNotice({
        tone: 'red',
        title: 'Could not update this feedback record.',
        message: error?.message || 'Unknown error',
      });
      return;
    }
    // Committed — clear the key and record success before the best-effort
    // refresh so a failed reload never flips a real conversion to a red banner
    // (and never strands a cleared key that a retry would re-convert against).
    clearPendingFeedbackMutation(mutationIdentity);
    dirtyDraftFieldsRef.current.conversion.clear();
    syncUpdatedFeedback(response.data.feedback);
    setActionNotice({
      tone: 'green',
      title: 'Feedback converted',
      message:
        targetType === 'issue'
          ? `${response.data.feedback.title || response.data.feedback.id} was converted to an issue and refreshed the governed queue.`
          : `${response.data.feedback.title || response.data.feedback.id} was converted to an action and refreshed the governed queue.`,
    });
    await refreshQueueAndDetail();
  }

  async function removeSandboxTestPackage() {
    const packageId = detailItem?.sourcePackageId;
    if (!packageId || cleanupConfirmation !== packageId || cleanupReason.trim().length < 10) return;
    const reason = cleanupReason.trim();
    // The cleanup request rides the shared idempotency machinery: the SAME
    // key is retained across timeouts/5xx, so a retry after a lost response
    // replays the completed purge instead of hitting an ambiguous failure.
    const mutationIdentity = {
      actorUserId: user?.id,
      operation: 'sandbox-test-purge',
      resourceId: packageId,
    };
    const pending = pendingFeedbackMutation({
      ...mutationIdentity,
      signature: feedbackMutationSignature({
        packageId,
        confirmation: cleanupConfirmation,
        reason,
      }),
    });
    setActionNotice(null);
    const finishRemoved = async (message) => {
      setCleanupOpen(false);
      setCleanupConfirmation('');
      setCleanupReason('');
      setSelectedId(null);
      // Record the removal BEFORE the refresh and swallow refresh errors: the
      // purge committed and the record is already deselected, so a failed queue
      // reload must not be reported as a failed removal.
      setActionNotice({ tone: 'green', title: 'Sandbox test feedback removed', message });
      try {
        await feedback.refresh();
      } catch {
        /* best-effort — the purge committed; the server remains authoritative */
      }
      noticeRef.current?.focus?.();
    };
    try {
      await mutation.run(() =>
        api.delete(`/feedback/sandbox-test-package/${packageId}`, {
          body: { confirmation: cleanupConfirmation, reason },
          headers: { 'Idempotency-Key': pending.key },
        }),
      );
      clearPendingFeedbackMutation(mutationIdentity);
      // A replayed success is a success — the package is gone either way.
      await finishRemoved(
        `${packageId} and its governed records were removed from this sandbox and queued for central purge.`,
      );
    } catch (error) {
      if (error?.code === 'FEEDBACK_PACKAGE_ALREADY_PURGED') {
        // Definitive terminal outcome: the package was already removed.
        clearPendingFeedbackMutation(mutationIdentity);
        await finishRemoved(`${packageId} was already removed from this sandbox.`);
        return;
      }
      if (!shouldRetainFeedbackMutation(error)) clearPendingFeedbackMutation(mutationIdentity);
      setActionNotice({
        tone: 'red',
        title: 'Could not remove sandbox test feedback.',
        message: shouldRetainFeedbackMutation(error)
          ? `${error?.message || 'The response was lost.'} Retry — your request keeps the same cleanup key, so it cannot run twice.`
          : error?.message || 'Unknown error',
      });
    }
  }

  if (feedback.loading) return <LoadingSkeleton rows={6} />;
  if (feedback.error) return <ErrorState error={feedback.error} />;

  const related = relatedRecordDescriptor(detailItem || {});
  const relatedHref = relatedRecordHref(detailItem || {});
  const conversionAvailable = canConvertFeedback(detailItem?.status);
  // Round 3 (R3-H02): shared-contract eligibility from the persisted status.
  const requestInfoEligibility = evaluateRequestMoreInfoEligibility(detailItem?.status);
  const trimmedTriageNote = triageNote.trim();
  const triageNoteOverLimit = trimmedTriageNote.length > FEEDBACK_COMMENT_MAX_LENGTH;
  const mirrorReadOnly = Boolean(detailItem?.mirrorReadOnly);
  const contextData = aggregateContext.data?.data || null;
  const governedSources = contextData?.sources || [];
  // The coverage strip is a CENTRAL surface: only the environment that
  // aggregates sources (its context lists a governed source map) renders it.
  // Inside a UAT sandbox the same page runs with no sources and must not
  // claim "central is awaiting UAT-01..05".
  const isCentralPosture = governedSources.length > 0;
  const sourceStatuses = aggregateStatus.data?.data || [];
  const sourceStatusLoading = !aggregateStatus.data && !aggregateStatus.error;
  const degradedSources = sourceStatuses.filter((status) => status.state !== 'current');
  const sourceBaseUrlByEnvironment = new Map(
    governedSources
      .filter((source) => source.baseUrl)
      .map((source) => [source.environmentId, source]),
  );
  // Environment facets come from the governed source map when the server
  // provides one — a hardcoded uat-01..05 list cannot follow configuration.
  const environmentOptions = governedSources.length
    ? [
        { value: '', label: 'All environments' },
        { value: contextData?.environmentId || 'pmhub', label: 'PMHub' },
        ...governedSources.map((source) => ({
          value: source.environmentId,
          label: source.label || source.environmentId.toUpperCase(),
        })),
      ]
    : ENVIRONMENT_OPTIONS;

  return (
    <>
      <PageHeader
        eyebrow="OWHA Adoption"
        title="Feedback Queue"
        subtitle="Element-specific feedback records with governed triage, conversion, and audit posture."
      />
      {actionNotice ? (
        <section
          className={`banner ${actionNotice.tone}`}
          aria-live="polite"
          role={actionNotice.tone === 'red' ? 'alert' : 'status'}
          ref={noticeRef}
          tabIndex={-1}
        >
          <div>
            <strong>{actionNotice.title}</strong>
            <p style={{ margin: 0 }}>{actionNotice.message}</p>
          </div>
        </section>
      ) : null}
      {isReviewer && isCentralPosture ? (
        sourceStatusLoading ? (
          <section className="feedback-source-status" role="status">
            <div>
              <strong>Feedback source coverage</strong>
              <p>Checking source freshness…</p>
            </div>
          </section>
        ) : aggregateStatus.error ? (
          <section className="feedback-source-status is-incomplete" role="status">
            <div>
              <strong>Feedback source coverage</strong>
              <p>
                Source status is unavailable right now — coverage is unknown, not necessarily
                complete. Source records remain authoritative.
              </p>
            </div>
          </section>
        ) : (
          <section
            className={`feedback-source-status${degradedSources.length ? ' is-incomplete' : ''}`}
            role={degradedSources.length ? 'alert' : 'status'}
            aria-live="polite"
            data-testid="feedback-source-strip"
          >
            <div>
              <strong>Feedback source coverage</strong>
              <p>
                {degradedSources.length
                  ? `Central results may be incomplete: ${degradedSources
                      .map(
                        (status) =>
                          `${(status.label || status.environmentId).toUpperCase()} is ${status.state}`,
                      )
                      .join(', ')}. Source records remain authoritative.`
                  : 'All configured UAT sources are current.'}
              </p>
            </div>
            <div className="feedback-source-status__chips" aria-label="Feedback source freshness">
              {sourceStatuses.map((status) => (
                <span
                  className={`fbk-source-badge state-${status.state}`}
                  key={status.environmentId}
                  data-testid={`feedback-source-chip-${status.environmentId}`}
                  title={[
                    status.heartbeatAt
                      ? `Last heartbeat ${formatTimestamp(status.heartbeatAt)}`
                      : 'No heartbeat yet',
                    status.coverageThroughAt
                      ? `Coverage through ${formatTimestamp(status.coverageThroughAt)}`
                      : null,
                    status.lastError
                      ? `Last error: ${String(status.lastError).slice(0, 160)}`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                >
                  {(status.label || status.environmentId).toUpperCase()} · {status.state}
                  {status.pendingCount > 0 ? ` · ${status.pendingCount} pending` : ''}
                </span>
              ))}
            </div>
          </section>
        )
      ) : null}
      <KpiHeroBand
        eyebrow="Feedback queue posture"
        asOf={feedback.loadedAt}
        items={kpiItems}
        columns={3}
      />
      <section className="feedback-ops-panel">
        <div className="feedback-ops-filter-head">
          <div>
            <div className="eyebrow">Queue filters</div>
            <h2 className="card-title">Filters</h2>
          </div>
          <button
            type="button"
            className="btn outline sm"
            onClick={clearFilters}
            disabled={!hasActiveFilters}
          >
            Clear filters
          </button>
        </div>
        <div className="feedback-ops-filter-grid">
          {isReviewer ? (
            <FormField label="Environment" htmlFor="feedback-environment">
              <Select
                id="feedback-environment"
                value={filters.environmentId}
                onChange={(value) => updateFilter('environmentId', value)}
                options={environmentOptions}
              />
            </FormField>
          ) : null}
          <FormField label="Queue view" htmlFor="feedback-queue-view">
            <Select
              id="feedback-queue-view"
              value={filters.queueView}
              onChange={(value) => updateFilter('queueView', value)}
              options={QUEUE_VIEW_OPTIONS}
            />
          </FormField>
          <FormField label="Status" htmlFor="feedback-filter-status">
            <Select
              id="feedback-filter-status"
              value={filters.status}
              onChange={(value) => updateFilter('status', value)}
              options={[{ value: '', label: 'All statuses' }, ...STATUS_OPTIONS]}
            />
          </FormField>
          <FormField label="Severity" htmlFor="feedback-filter-severity">
            <Select
              id="feedback-filter-severity"
              value={filters.severity}
              onChange={(value) => updateFilter('severity', value)}
              options={[{ value: '', label: 'All severities' }, ...SEVERITY_OPTIONS]}
            />
          </FormField>
          <FormField label="Route or page" htmlFor="feedback-filter-route">
            <TextInput
              id="feedback-filter-route"
              value={filters.routeQuery}
              onChange={(value) => updateFilter('routeQuery', value)}
              placeholder="Search route, page, or workflow"
            />
          </FormField>
          <FormField label="Component" htmlFor="feedback-filter-component">
            <TextInput
              id="feedback-filter-component"
              value={filters.componentQuery}
              onChange={(value) => updateFilter('componentQuery', value)}
              placeholder="Search component or element"
            />
          </FormField>
          <FormField label="Feature area" htmlFor="feedback-filter-feature">
            <TextInput
              id="feedback-filter-feature"
              value={filters.featureArea}
              onChange={(value) => updateFilter('featureArea', value)}
              placeholder="Search category or feature area"
            />
          </FormField>
          <FormField label="UAT cohort" htmlFor="feedback-filter-cohort">
            <TextInput
              id="feedback-filter-cohort"
              value={filters.uatCohort}
              onChange={(value) => updateFilter('uatCohort', value)}
              placeholder="Search cohort"
            />
          </FormField>
          {/* Typed-intake facets (commit 6). Server-side, so the count pill and
              the KPI band above describe the filtered slice rather than the
              whole queue. */}
          <FormField label="Entry type" htmlFor="feedback-filter-entry-type">
            <Select
              id="feedback-filter-entry-type"
              value={filters.entryType}
              onChange={(value) => updateFilter('entryType', value)}
              options={ENTRY_TYPE_OPTIONS}
            />
          </FormField>
          <FormField label="Request origin" htmlFor="feedback-filter-has-intake">
            <Select
              id="feedback-filter-has-intake"
              value={filters.hasIntake}
              onChange={(value) => updateFilter('hasIntake', value)}
              options={INTAKE_LINK_OPTIONS}
            />
          </FormField>
          <FormField label="Originating request" htmlFor="feedback-filter-source-intake">
            <TextInput
              id="feedback-filter-source-intake"
              value={filters.sourceIntakeId}
              onChange={(value) => updateFilter('sourceIntakeId', value)}
              placeholder="FINT-…"
            />
          </FormField>
        </div>
      </section>
      <section className="feedback-ops-workspace">
        <section className="feedback-ops-list-column">
          <article className="feedback-ops-panel">
            <div className="feedback-ops-filter-head">
              <div>
                <div className="eyebrow">Queue workspace</div>
                <h2 className="card-title">Feedback list</h2>
              </div>
              <span className="feedback-ops-count-pill">
                {filteredRows.length} shown of {queue.page.total}
              </span>
            </div>
            {severityDistribution.length ? (
              <div className="fbk-rdx-summary">
                <StatusDonutMini
                  segments={severityDistribution}
                  size={104}
                  totalLabel="Records"
                  ariaLabel="Feedback severity distribution"
                />
                <div className="fbk-rdx-summary__metrics">
                  <div className="fbk-rdx-summary__metric">
                    <div className="eyebrow">Severity mix</div>
                    <strong>{rows.length}</strong>
                    <span className="muted">loaded governed feedback records</span>
                  </div>
                  {severityDistribution.map((segment) => (
                    <div className="fbk-rdx-summary__metric" key={segment.label}>
                      <div className="eyebrow">{segment.label}</div>
                      <strong>{segment.count}</strong>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            {rows.length === 0 ? (
              <EmptyState
                title="No governed feedback records found"
                description="Element-specific submissions will appear here once users start capture or queue triage."
              />
            ) : filteredRows.length === 0 ? (
              <EmptyState
                title="No feedback matched these filters"
                description="Clear or relax the queue filters to review a different slice of governed feedback."
              />
            ) : (
              <StaggerReveal className="feedback-ops-stack">
                {filteredRows.map((item) => (
                  <FeedbackCard
                    key={item.id}
                    item={item}
                    selected={item.id === selectedId}
                    onSelect={selectRecord}
                  />
                ))}
              </StaggerReveal>
            )}
            {queue.appendError ? (
              <div className="feedback-ops-pagination-error" role="alert">
                <span>
                  More feedback could not be loaded. Your current results are still available.
                </span>
                <button
                  type="button"
                  className="btn outline sm"
                  onClick={() => loadQueuePage({ cursor: queue.page.nextCursor, append: true })}
                  disabled={queue.loadingMore || !queue.page.nextCursor}
                >
                  Retry
                </button>
              </div>
            ) : null}
            {queue.page.hasMore ? (
              <div className="feedback-ops-load-more">
                <button
                  type="button"
                  className="btn outline"
                  onClick={() => loadQueuePage({ cursor: queue.page.nextCursor, append: true })}
                  disabled={queue.loadingMore || !queue.page.nextCursor}
                >
                  {queue.loadingMore ? 'Loading feedback…' : 'Load more feedback'}
                </button>
              </div>
            ) : null}
          </article>
        </section>
        <section className="feedback-ops-detail-column">
          {!selectedId ? (
            <article className="feedback-ops-panel">
              <EmptyState
                title="Select a feedback record"
                description="Choose a governed feedback item from the queue to review its screenshot, history, and triage actions."
              />
            </article>
          ) : detailLoadingForSelection ? (
            <LoadingSkeleton rows={5} />
          ) : detail.error ? (
            <ErrorState error={detail.error} />
          ) : (
            <div className="feedback-ops-detail-stack">
              <article className="feedback-ops-panel">
                <div className="row spread" style={{ gap: 12 }}>
                  <div>
                    <div className="fbk-source-line">
                      <span className="eyebrow">{detailItem?.originId || detailItem?.id}</span>
                      <span className="fbk-source-badge">
                        {String(detailItem?.environmentId || 'pmhub').toUpperCase()}
                      </span>
                    </div>
                    <h2 className="card-title">Selected feedback detail</h2>
                  </div>
                  <StatusChip status={detailItem?.status || 'new'} />
                </div>
                <div className="fbk-rdx-pipeline-wrap">
                  <WorkflowPipeline
                    steps={buildTriagePipeline(detailItem)}
                    ariaLabel="Feedback triage lifecycle"
                  />
                </div>
                <p className="feedback-ops-detail-summary">{detailItem?.description}</p>
                <div className="feedback-ops-detail-grid">
                  <div>
                    <span className="eyebrow">Reporter</span>
                    <strong>{reporterLabel(detailItem) || 'Reporter unavailable'}</strong>
                  </div>
                  <div>
                    <span className="eyebrow">Page</span>
                    <strong>
                      {detailItem?.pageTitle ||
                        detailItem?.screenContext?.pageTitle ||
                        'Page unavailable'}
                    </strong>
                  </div>
                  <div>
                    <span className="eyebrow">Route</span>
                    <strong>{detailItem?.routePath || 'Route unavailable'}</strong>
                  </div>
                  <div>
                    <span className="eyebrow">Component</span>
                    {/* H2 — human element label as the primary value; the raw
                        componentKey stays as a small monospace machine reference. */}
                    <strong>
                      {detailItem?.elementLabel ||
                        detailItem?.componentKey ||
                        'Component unavailable'}
                    </strong>
                    {detailItem?.elementLabel && detailItem?.componentKey ? (
                      <code style={COMPONENT_KEY_STYLE}>{detailItem.componentKey}</code>
                    ) : null}
                  </div>
                  <div>
                    <span className="eyebrow">Entity</span>
                    <strong>{entityLabel(detailItem) || 'No entity link'}</strong>
                  </div>
                  <div>
                    <span className="eyebrow">Captured</span>
                    <strong>
                      {formatTimestamp(
                        detailItem?.capturedAt || detailItem?.screenContext?.capturedAt,
                      )}
                    </strong>
                  </div>
                </div>
                {detailItem?.expectedBehavior ||
                detailItem?.actualBehavior ||
                detailItem?.screenContext?.annotationInstruction ? (
                  <div className="feedback-ops-context">
                    {detailItem?.expectedBehavior ? (
                      <section>
                        <strong>Expected behavior</strong>
                        <p>{detailItem.expectedBehavior}</p>
                      </section>
                    ) : null}
                    {detailItem?.actualBehavior ? (
                      <section>
                        <strong>Actual behavior</strong>
                        <p>{detailItem.actualBehavior}</p>
                      </section>
                    ) : null}
                    {detailItem?.screenContext?.annotationInstruction ? (
                      <section>
                        <strong>Capture note</strong>
                        <p>{detailItem.screenContext.annotationInstruction}</p>
                      </section>
                    ) : null}
                  </div>
                ) : null}
              </article>
              {/* Type-specific reviewer material (commit 6). Renders NOTHING for
                  a screenshot-feedback record, so the detail pane a reviewer
                  already knew is unchanged for the records it already held. */}
              <TypedIntakeDetailSections
                item={detailItem}
                onFilterBySourceIntake={(intakeId) => {
                  setFilters((current) => ({ ...current, sourceIntakeId: intakeId }));
                }}
              />
              <article className="feedback-ops-panel">
                <div className="eyebrow">Preview</div>
                <h3 className="card-title">Screenshot / annotation preview</h3>
                <GovernedAnnotationPreview
                  screenContext={detailItem?.screenContext}
                  environmentId={
                    detailItem?.mirrorReadOnly
                      ? detailItem?.sourceEnvironmentId || detailItem?.environmentId
                      : 'local'
                  }
                  alt={`${detailItem?.title || detailItem?.id || 'Feedback'} capture`}
                />
              </article>
              <article className="feedback-ops-panel">
                <div className="eyebrow">Traceability</div>
                <h3 className="card-title">Related record link</h3>
                {related.id ? (
                  mirrorReadOnly ? (
                    // A mirrored record's related id belongs to the SOURCE
                    // environment. A local route would resolve the id against
                    // PMHub's own records (INV-05) — so mirrors get an
                    // explicit, named environment transition or plain text,
                    // never a local <Link>.
                    (() => {
                      const sourceEnvironmentId =
                        detailItem?.sourceEnvironmentId || detailItem?.environmentId || '';
                      const source = sourceBaseUrlByEnvironment.get(sourceEnvironmentId);
                      const relatedPath = detailItem?.sourceRelatedRecordPath || null;
                      if (source && relatedPath) {
                        return (
                          <a
                            className="feedback-ops-related-link"
                            data-testid="feedback-mirror-related-link"
                            href={`${source.baseUrl}${relatedPath}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Open related record in{' '}
                            {source.label || sourceEnvironmentId.toUpperCase()} ↗
                          </a>
                        );
                      }
                      return (
                        <p
                          className="feedback-ops-related-link"
                          data-testid="feedback-mirror-related-descriptor"
                        >
                          {`${humanizeToken(related.type, related.type)} · ${related.id} · managed in ${sourceEnvironmentId.toUpperCase()}`}
                        </p>
                      );
                    })()
                  ) : relatedHref ? (
                    <Link className="feedback-ops-related-link" to={relatedHref}>
                      {humanizeToken(related.type, related.type)} · {related.id}
                    </Link>
                  ) : (
                    <p className="feedback-ops-related-link">{`${humanizeToken(related.type, related.type)} · ${related.id}`}</p>
                  )
                ) : (
                  <p className="muted">No related record is linked yet.</p>
                )}
                {/* AC-FBK-1.2: a record created from a captured feedback
                    package links back to that package. */}
                {detailItem?.sourcePackageId ? (
                  <p className="feedback-ops-related-link">
                    {mirrorReadOnly ? (
                      (() => {
                        const sourceEnvironmentId =
                          detailItem?.sourceEnvironmentId || detailItem?.environmentId || '';
                        const source = sourceBaseUrlByEnvironment.get(sourceEnvironmentId);
                        const packagePath =
                          detailItem?.sourcePackagePath ||
                          `/feedback/${encodeURIComponent(detailItem.sourcePackageId)}`;
                        if (source) {
                          return (
                            <a
                              data-testid="feedback-source-package-link"
                              href={`${source.baseUrl}${packagePath}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Open source package in{' '}
                              {source.label || sourceEnvironmentId.toUpperCase()} ↗
                            </a>
                          );
                        }
                        return (
                          <span data-testid="feedback-source-package-link">
                            Source package · {detailItem.sourcePackageId} · managed in{' '}
                            {sourceEnvironmentId.toUpperCase()}
                          </span>
                        );
                      })()
                    ) : (
                      <Link
                        data-testid="feedback-source-package-link"
                        to={`/feedback/${detailItem.sourcePackageId}`}
                      >
                        Source package · {detailItem.sourcePackageId}
                      </Link>
                    )}
                  </p>
                ) : null}
                {aggregateContext.data?.data?.sandboxCleanupEnabled &&
                detailItem?.sourcePackageId &&
                !mirrorReadOnly ? (
                  <div className="feedback-sandbox-cleanup">
                    {!cleanupOpen ? (
                      <button
                        type="button"
                        className="btn outline sm"
                        onClick={() => setCleanupOpen(true)}
                      >
                        Remove sandbox test package
                      </button>
                    ) : (
                      <div
                        className="feedback-sandbox-cleanup__form"
                        role="group"
                        aria-label="Remove sandbox test package"
                      >
                        <p>
                          This governed sandbox action removes the submitted package and all of its
                          feedback records. Type <strong>{detailItem.sourcePackageId}</strong>{' '}
                          exactly.
                        </p>
                        <FormField
                          label="Package ID confirmation"
                          htmlFor="feedback-cleanup-confirmation"
                        >
                          <TextInput
                            id="feedback-cleanup-confirmation"
                            value={cleanupConfirmation}
                            onChange={setCleanupConfirmation}
                          />
                        </FormField>
                        <FormField label="Cleanup reason" htmlFor="feedback-cleanup-reason">
                          <Textarea
                            id="feedback-cleanup-reason"
                            value={cleanupReason}
                            onChange={setCleanupReason}
                            rows={3}
                            placeholder="Synthetic UAT proof completed and reconciled."
                          />
                        </FormField>
                        <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
                          <button
                            type="button"
                            className="btn outline sm"
                            onClick={() => setCleanupOpen(false)}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="btn danger sm"
                            onClick={removeSandboxTestPackage}
                            disabled={
                              mutation.running ||
                              cleanupConfirmation !== detailItem.sourcePackageId ||
                              cleanupReason.trim().length < 10
                            }
                          >
                            Remove test package
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
              </article>
              <article className="feedback-ops-panel">
                <div className="eyebrow">Triage</div>
                <h3 className="card-title">Triage actions</h3>
                {mirrorReadOnly ? (
                  <p className="feedback-mirror-readonly" role="status">
                    Managed in {String(detailItem?.environmentId || '').toUpperCase()}. This central
                    copy is read-only; triage and replies remain authoritative in the source
                    environment.
                  </p>
                ) : null}
                {!isReviewer ? (
                  // W3B: only System Admin reviewers can triage — rendering
                  // dead controls that 403 was a latent trap for queue roles.
                  <p className="muted" data-testid="feedback-triage-readonly">
                    Triage decisions (status, ownership, replies) are made by the System Admin
                    product team. This queue is read-only for your role.
                  </p>
                ) : null}
                {isReviewer && !mirrorReadOnly ? (
                  <div className="feedback-ops-form-grid">
                    <FormField label="Owner" htmlFor="feedback-owner">
                      <Select
                        id="feedback-owner"
                        value={triageDraft.ownerUserId}
                        onChange={(value) => updateTriageDraft('ownerUserId', value)}
                        options={[{ value: '', label: 'Unassigned' }, ...ownerChoices]}
                      />
                    </FormField>
                    <FormField label="Workflow status" htmlFor="feedback-status">
                      <Select
                        id="feedback-status"
                        value={triageDraft.status}
                        onChange={(value) => updateTriageDraft('status', value)}
                        options={STATUS_OPTIONS}
                      />
                    </FormField>
                    <FormField label="Severity level" htmlFor="feedback-severity">
                      <Select
                        id="feedback-severity"
                        value={triageDraft.severity}
                        onChange={(value) => updateTriageDraft('severity', value)}
                        options={SEVERITY_OPTIONS}
                      />
                    </FormField>
                    <FormField label="Category" htmlFor="feedback-category">
                      <Select
                        id="feedback-category"
                        value={triageDraft.category}
                        onChange={(value) => updateTriageDraft('category', value)}
                        options={categories}
                      />
                    </FormField>
                    <FormField label="Assigned UAT cohort" htmlFor="feedback-uat-cohort">
                      <TextInput
                        id="feedback-uat-cohort"
                        value={triageDraft.uatCohort}
                        onChange={(value) => updateTriageDraft('uatCohort', value)}
                        placeholder="Wave 1"
                      />
                    </FormField>
                    <FormField label="Related record type" htmlFor="feedback-related-type">
                      <Select
                        id="feedback-related-type"
                        value={triageDraft.linkedRecordType}
                        onChange={(value) => updateTriageDraft('linkedRecordType', value)}
                        options={RELATED_RECORD_TYPE_OPTIONS}
                      />
                    </FormField>
                    <FormField label="Related record ID" htmlFor="feedback-related-id">
                      <TextInput
                        id="feedback-related-id"
                        value={triageDraft.linkedRecordId}
                        onChange={(value) => updateTriageDraft('linkedRecordId', value)}
                        placeholder="ACT-OWHA-001"
                      />
                    </FormField>
                    <FormField label="Resolution note" htmlFor="feedback-resolution-note">
                      <Textarea
                        id="feedback-resolution-note"
                        value={triageDraft.resolutionNote}
                        onChange={(value) => updateTriageDraft('resolutionNote', value)}
                        placeholder="Record the latest queue decision or resolution."
                        rows={4}
                      />
                    </FormField>
                  </div>
                ) : null}
                {isReviewer && !mirrorReadOnly ? (
                  <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
                    <button
                      type="button"
                      className="btn primary sm"
                      onClick={saveTriageChanges}
                      disabled={mutation.running}
                    >
                      Save triage changes
                    </button>
                  </div>
                ) : null}
                <div className="feedback-ops-note-block">
                  {isReviewer && !mirrorReadOnly ? (
                    <>
                      <FormField label="Triage note" htmlFor="feedback-triage-note">
                        {/* No maxLength: over-limit text is PRESERVED and marked
                            invalid rather than silently truncated (Round 3). */}
                        <Textarea
                          id="feedback-triage-note"
                          value={triageNote}
                          onChange={setTriageNote}
                          placeholder="Capture the latest triage note, blocker, or follow-up."
                          rows={3}
                          ariaInvalid={triageNoteOverLimit ? 'true' : undefined}
                          ariaDescribedBy="feedback-triage-note-count"
                        />
                      </FormField>
                      <p
                        id="feedback-triage-note-count"
                        className={triageNoteOverLimit ? 'feedback-error-text' : 'muted'}
                        data-testid="feedback-triage-note-count"
                        role={triageNoteOverLimit ? 'alert' : undefined}
                        style={{ margin: '4px 0 0', textAlign: 'right' }}
                      >
                        {triageNoteOverLimit
                          ? `${trimmedTriageNote.length - FEEDBACK_COMMENT_MAX_LENGTH} characters over the ${FEEDBACK_COMMENT_MAX_LENGTH} limit`
                          : `${FEEDBACK_COMMENT_MAX_LENGTH - triageNote.length} characters left`}
                      </p>
                      {!requestInfoEligibility.eligible ? (
                        <p
                          id="feedback-request-more-info-reason"
                          className="muted"
                          data-testid="feedback-request-more-info-reason"
                          style={{ margin: '4px 0 0', textAlign: 'right' }}
                        >
                          {requestInfoEligibility.message}
                        </p>
                      ) : null}
                      <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
                        <button
                          type="button"
                          className="btn outline sm"
                          data-testid="feedback-request-more-info"
                          onClick={requestMoreInfo}
                          disabled={
                            mutation.running ||
                            !trimmedTriageNote ||
                            triageNoteOverLimit ||
                            !requestInfoEligibility.eligible
                          }
                          aria-describedby={
                            requestInfoEligibility.eligible
                              ? undefined
                              : 'feedback-request-more-info-reason'
                          }
                          title="Sets the status to Needs more info and sends your question to the reporter"
                        >
                          Request more info
                        </button>
                        <button
                          type="button"
                          className="btn outline sm"
                          onClick={addTriageNote}
                          disabled={mutation.running || !trimmedTriageNote || triageNoteOverLimit}
                        >
                          Add triage note
                        </button>
                      </div>
                    </>
                  ) : null}
                  {detailItem?.comments?.length ? (
                    <ul className="feedback-ops-history-list">
                      {detailItem.comments.map((comment) => (
                        <li
                          key={comment.id}
                          className={
                            comment.authorRole === 'reporter' ? 'feedback-ops-reporter-comment' : ''
                          }
                        >
                          <strong>
                            {comment.authorRole === 'reporter'
                              ? `Reporter · ${comment.authorUserId || 'reporter'}`
                              : comment.authorUserId || 'Workspace author'}
                          </strong>{' '}
                          · {formatTimestamp(comment.createdAt)}
                          <p>{comment.body}</p>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="muted">
                      No triage notes have been recorded for this feedback item yet.
                    </p>
                  )}
                </div>
              </article>
              {isReviewer && !mirrorReadOnly ? (
                <article className="feedback-ops-panel">
                  <div className="eyebrow">Conversion</div>
                  <h3 className="card-title">Conversion actions</h3>
                  <p className="muted">
                    Create a governed follow-through record while preserving the source feedback
                    link, assigned owner, priority, and due date.
                  </p>
                  {!conversionAvailable ? (
                    <p className="muted" data-testid="feedback-conversion-unavailable">
                      Conversion is unavailable because this feedback is not in a convertible
                      lifecycle state.
                    </p>
                  ) : null}
                  <div className="feedback-ops-form-grid" style={{ marginTop: 16 }}>
                    <FormField label="Conversion owner" htmlFor="feedback-conversion-owner">
                      <Select
                        id="feedback-conversion-owner"
                        value={conversionDraft.ownerUserId}
                        onChange={(value) => updateConversionDraft('ownerUserId', value)}
                        options={[{ value: '', label: 'Use governed default' }, ...ownerChoices]}
                      />
                    </FormField>
                    <FormField label="Conversion priority" htmlFor="feedback-conversion-priority">
                      <Select
                        id="feedback-conversion-priority"
                        value={conversionDraft.priority}
                        onChange={(value) => updateConversionDraft('priority', value)}
                        options={CONVERSION_PRIORITY_OPTIONS}
                      />
                    </FormField>
                    <FormField label="Conversion due date" htmlFor="feedback-conversion-due-date">
                      <DateInput
                        id="feedback-conversion-due-date"
                        value={conversionDraft.dueDate}
                        onChange={(value) => updateConversionDraft('dueDate', value)}
                      />
                    </FormField>
                  </div>
                  <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
                    <button
                      type="button"
                      className="btn ghost sm"
                      onClick={() => convertFeedback('issue')}
                      disabled={mutation.running || !conversionAvailable}
                    >
                      Convert to issue
                    </button>
                    <button
                      type="button"
                      className="btn primary sm"
                      data-testid="feedback-convert-action"
                      onClick={() => convertFeedback('action')}
                      disabled={mutation.running || !conversionAvailable}
                    >
                      Convert to action
                    </button>
                  </div>
                </article>
              ) : null}
              <article className="feedback-ops-panel">
                <div className="eyebrow">Audit</div>
                <h3 className="card-title">Audit history</h3>
                {detailItem?.auditHistory?.length ? (
                  <ol className="fbk-rdx-timeline">
                    {detailItem.auditHistory.map((event) => (
                      <li key={event.id} className="fbk-rdx-timeline__item">
                        <span className="fbk-rdx-timeline__dot" aria-hidden />
                        <div className="fbk-rdx-timeline__head">
                          <span className="fbk-rdx-timeline__action">
                            {humanizeToken(event.action, 'Audit event')}
                          </span>
                          <span className="fbk-rdx-timeline__meta">
                            {event.actorUserId || 'Workspace actor'} ·{' '}
                            {formatTimestamp(event.occurredAt)}
                          </span>
                        </div>
                        <p className="fbk-rdx-timeline__body">{summarizeAuditEvent(event)}</p>
                        {summarizeAuditChanges(event) ? (
                          <p
                            className="fbk-rdx-timeline__body muted"
                            data-testid={`feedback-audit-changes-${event.id}`}
                          >
                            {summarizeAuditChanges(event)}
                          </p>
                        ) : null}
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p className="muted">No audit events are available for this feedback item yet.</p>
                )}
              </article>
            </div>
          )}
        </section>
      </section>
    </>
  );
}
